import { Appointment } from 'vl-common/src/schemas/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import * as actions from '@actions';
import { useEffect } from 'react';
import { useUserSafe } from '@vl-core/hooks/useUser';

/** Subscribes to the shared bulk status query and selects one row for this appointment id. */
export function useBulkAppointmentStatusRow(appointmentId: number | undefined) {
  const dispatch = useDispatch();
  const user = useUserSafe();

  const enabled =
    !!user &&
    ['PATIENT', 'CLINICIAN'].includes(user.user_type_code) &&
    appointmentId != null &&
    Number.isFinite(appointmentId);

  return useQuery({
    queryKey: ['getAppointmentStatus'],
    queryFn: () => dispatch(actions.getTodaysAppointmentsAndStatuses()),
    select: (data) => data.appointment_stati?.find((s) => s.appt_id === appointmentId) ?? null,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    enabled
  });
}

export function useMissedVideoCall(appointment: Appointment) {
  const queryClient = useQueryClient();
  const { data: statusRow } = useBulkAppointmentStatusRow(appointment.appointment_id);

  useEffect(() => {
    const now = new Date();
    if (appointment.end <= now) return;

    const jitter = Math.floor(Math.random() * 25000) + 5000;
    const delay = Math.max(0, appointment.end.getTime() - now.getTime() + jitter);
    const t = setTimeout(() => {
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['getAppointmentStatus'] }),
        queryClient.invalidateQueries({ queryKey: ['getUserAppointment'] })
      ]).then();
    }, delay);
    return () => clearTimeout(t);
  }, [appointment.appointment_id, appointment.end, queryClient]);

  if (!statusRow) {
    return false;
  }

  const { clin_start } = statusRow;

  if (clin_start) return false;

  return appointment.end < new Date();
}
